/**
 * Global tracker for long-running AI background jobs.
 *
 * Currently tracks two kinds of jobs that share the "kick-off + poll" pattern:
 *   - `split` — POST /chapters/:id/lessons/ai-split-pdf   (multi-batch lesson split)
 *   - `quiz`  — POST /chapters/:id/quiz/auto-generate     (auto-draft 20 Qs from lessons)
 *
 * Why a global tracker (vs polling inside each dialog)?
 *   • The user wants to queue multiple chapters at once. Dialogs are per-chapter
 *     and get dismissed; a global tracker is the only place state can live
 *     across chapters.
 *   • The bottom-right `AiJobsTray` widget renders live progress bars from this
 *     tracker, so progress stays visible even when the user navigates between
 *     pages.
 *   • Job IDs persist in localStorage so progress survives a refresh — on next
 *     mount we re-poll the backend for the latest state.
 */
import React, {
    createContext, useContext, useEffect, useRef, useState, useCallback,
} from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const STORAGE_KEY = "cco_ai_jobs_v2";
const POLL_MS = 2000;

const AiJobsContext = createContext(null);

/* ------------------- per-kind config ------------------- */

/**
 * Per-kind plumbing (status URL, completion toast, percent calculator).
 * Adding a new background-job kind is just adding an entry to this map.
 */
const KIND_CONFIG = {
    split: {
        label: "AI split",
        statusUrl: ({ chapterId, jobId }) =>
            `/chapters/${chapterId}/lessons/ai-split-pdf/status?job_id=${jobId}`,
        successMessage: (job, data) =>
            `${data.lessons_created || 0} lesson${(data.lessons_created || 0) === 1 ? "" : "s"} drafted in "${job.chapterTitle}".`,
        computePercent: (prev, job) => {
            const total = Math.max(1, Number(job.total) || Number(job.numLessons) || 5);
            const done = Number(job.progress) || 0;
            const totalBatches = Math.max(1, Number(job.total_batches) || 1);
            const currentBatch = Math.max(0, Number(job.current_batch) || 0);
            if (job.status === "done") return 100;
            if (job.status === "failed") return prev?.percent ?? 0;
            let pct = (done / total) * 100;
            if (job.stage === "ai-drafting" && currentBatch > 0) {
                const batchFloor = ((currentBatch - 1) / totalBatches) * 100;
                const batchCeil = (currentBatch / totalBatches) * 100;
                const creep = Math.min(
                    batchCeil - 1,
                    (prev?.percent ?? batchFloor) + 0.8,
                );
                pct = Math.max(pct, creep);
            } else if (job.stage === "queued") {
                pct = Math.max(2, pct);
            } else if (job.stage === "extracting") {
                // creep from 3 → 8% during fast digital-PDF extraction
                pct = Math.max(prev?.percent ?? 3, Math.min(8, (prev?.percent ?? 3) + 0.3));
            } else if (job.stage === "ocr") {
                // OCR is the slow path for scanned PDFs (~3-6s per page).
                // We get explicit per-page progress from the backend so we
                // can grow the bar smoothly between 8% and 30% (the bound
                // before AI drafting starts).
                const ocrCur = Number(job.ocr_current) || 0;
                const ocrTot = Math.max(1, Number(job.ocr_total) || 1);
                const ocrPct = 8 + (ocrCur / ocrTot) * 22;  // 8% → 30%
                pct = Math.max(prev?.percent ?? 8, ocrPct);
            }
            return Math.max(prev?.percent ?? 0, Math.min(99, pct));
        },
        stageLabel: (job) => {
            if (job.status === "done") {
                return `Done — ${job.lessons_created || 0} lesson${(job.lessons_created || 0) === 1 ? "" : "s"} drafted`;
            }
            if (job.status === "failed") return job.error || "Failed";
            if (job.stage === "queued") return "Queued for AI…";
            if (job.stage === "extracting") return "Reading PDF text…";
            if (job.stage === "ocr") {
                const cur = job.ocr_current || 0;
                const tot = job.ocr_total || 0;
                return tot > 0
                    ? `OCR scanned PDF — page ${cur}/${tot}…`
                    : "OCR scanned PDF…";
            }
            if (job.stage === "ai-drafting") {
                const cur = job.current_batch || 1;
                const tot = job.total_batches || 1;
                return tot > 1
                    ? `Drafting batch ${cur} of ${tot}…`
                    : "AI is drafting your lessons…";
            }
            if (job.stage === "saving") {
                return `Saving lessons (${job.progress || 0}/${job.total || job.numLessons})…`;
            }
            return "Working…";
        },
    },
    quiz: {
        label: "AI quiz",
        statusUrl: ({ chapterId, jobId }) =>
            `/chapters/${chapterId}/quiz/auto-generate/status?job_id=${jobId}`,
        successMessage: (job, data) => {
            const n = (data.quiz?.questions || []).length;
            return `Drafted ${n}-question quiz for "${job.chapterTitle}". Reopen the chapter quiz editor to review.`;
        },
        computePercent: (prev, job) => {
            if (job.status === "done") return 100;
            if (job.status === "failed") return prev?.percent ?? 0;
            // Quiz job has stages "harvesting" → "drafting questions" → "saving".
            // We mostly creep the bar forward steadily since the job doesn't
            // expose fine-grained progress for the drafting stage.
            const total = Math.max(1, Number(job.total) || 1);
            const done = Number(job.progress) || 0;
            let pct = (done / total) * 70; // harvest stage gets 0-70%
            if (job.stage === "drafting questions") {
                pct = Math.max(prev?.percent ?? 70, Math.min(95, (prev?.percent ?? 70) + 1));
            } else if (job.stage === "saving") {
                pct = 97;
            } else if (job.stage === "queued") {
                pct = Math.max(2, pct);
            }
            return Math.max(prev?.percent ?? 0, Math.min(99, pct));
        },
        stageLabel: (job) => {
            if (job.status === "done") return "Done — quiz drafted";
            if (job.status === "failed") return job.error || "Failed";
            if (job.stage === "queued") return "Queued for AI…";
            if (job.stage === "harvesting" || job.stage === "harvesting lessons") {
                return `Reading lessons (${job.progress || 0}/${job.total || 1})…`;
            }
            if (job.stage === "drafting questions") return "Drafting 20 questions…";
            if (job.stage === "saving") return "Saving quiz…";
            return "Working…";
        },
    },
    video: {
        label: "Render video",
        // Lesson-video jobs are keyed by job_id only (not chapter-scoped).
        statusUrl: ({ jobId }) => `/lesson-video-jobs/${jobId}`,
        successMessage: (job /* , data */) =>
            `Video ready for "${job.lessonTitle || job.chapterTitle || "your lesson"}".`,
        computePercent: (prev, job) => {
            if (job.status === "done") return 100;
            if (job.status === "failed") return prev?.percent ?? 0;
            // Queued (waiting for the render semaphore) → tiny static bar
            // so the user reads "Queued #3" without a misleading 5% fill.
            if (job.step === "queued" || job.status === "queued") {
                return 1;
            }
            const total = Math.max(1, Number(job.total) || 1);
            const cur = Math.min(total, Number(job.current) || 0);
            let pct = (cur / total) * 90 + 5;
            if (job.step === "stitching") pct = Math.max(pct, 96);
            return Math.max(prev?.percent ?? 0, Math.min(99, pct));
        },
        stageLabel: (job) => {
            if (job.status === "done") return "Done — video ready";
            if (job.status === "failed") return job.error || "Failed";
            // Queue position takes priority when waiting for the global
            // video-render semaphore to free up.
            if (job.step === "queued" || job.status === "queued") {
                const qp = Number(job.queue_position);
                if (Number.isFinite(qp) && qp > 0) {
                    return `Queued — #${qp} in line`;
                }
                return "Queued — next up";
            }
            if (job.step === "stitching") return "Stitching final MP4…";
            // Default: rendering individual slides.
            const cur = Math.min(job.total || 1, job.current || 0);
            return `Rendering slide ${cur || 1}/${job.total || 1}…`;
        },
    },
    pptx: {
        label: "Generate PPTX",
        statusUrl: ({ jobId }) => `/lesson-pptx-jobs/${jobId}`,
        successMessage: (job, data) => {
            const n = data?.pptx_slide_count || job?.total || 0;
            return `Deck ready for "${job.lessonTitle || "your lesson"}" — ${n} slide${n === 1 ? "" : "s"}.`;
        },
        computePercent: (prev, job) => {
            if (job.status === "done") return 100;
            if (job.status === "failed") return prev?.percent ?? 0;
            // Staged: queued (3%) → planning (5-55%) → rendering (55-90%) → uploading (90-98%).
            const stage = job.stage || "queued";
            let target = 3;
            if (stage === "planning") target = 45;
            else if (stage === "rendering") target = 80;
            else if (stage === "uploading") target = 95;
            // Creep slowly toward target so the bar feels alive even
            // though we don't get per-slide progress from python-pptx.
            const base = prev?.percent ?? 0;
            return Math.max(base, Math.min(target, base + 1.5));
        },
        stageLabel: (job) => {
            if (job.status === "done") return "Done — PPTX ready";
            if (job.status === "failed") return job.error || "Failed";
            if (job.stage === "planning") return "AI is planning slides…";
            if (job.stage === "rendering") return "Building PowerPoint deck…";
            if (job.stage === "uploading") return "Uploading deck…";
            return "Queued for AI…";
        },
    },
    chapter_pptx: {
        label: "Combined chapter PPTX",
        statusUrl: ({ jobId }) => `/chapter-pptx-jobs/${jobId}`,
        successMessage: (job, data) => {
            const n = data?.pptx_slide_count || 0;
            const ln = data?.lesson_count || job?.numLessons || 0;
            return `Chapter deck ready — ${ln} lesson${ln === 1 ? "" : "s"}, ${n} slides.`;
        },
        computePercent: (prev, job) => {
            if (job.status === "done") return 100;
            if (job.status === "failed") return prev?.percent ?? 0;
            const stage = job.stage || "queued";
            let target = 3;
            if (stage === "planning") target = 60;
            else if (stage === "rendering") target = 85;
            else if (stage === "uploading") target = 95;
            const base = prev?.percent ?? 0;
            return Math.max(base, Math.min(target, base + 1.5));
        },
        stageLabel: (job) => {
            if (job.status === "done") return "Done — combined deck ready";
            if (job.status === "failed") return job.error || "Failed";
            if (job.stage === "planning") return "AI planning slides for all lessons…";
            if (job.stage === "rendering") return "Building combined PowerPoint…";
            if (job.stage === "uploading") return "Uploading deck…";
            return "Queued for AI…";
        },
    },
};

/* ------------------- localStorage helpers ------------------- */

function loadStored() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return {};
        const arr = JSON.parse(raw);
        if (!Array.isArray(arr)) return {};
        const map = {};
        for (const j of arr) {
            // split/quiz need chapterId; video/pptx/chapter_pptx need jobId only.
            const ok = j && j.jobId && KIND_CONFIG[j.kind] && (
                j.kind === "video" || j.kind === "pptx" || j.kind === "chapter_pptx" || j.chapterId
            );
            if (ok) {
                map[j.jobId] = {
                    ...j,
                    status: j.kind === "video" || j.kind === "pptx" || j.kind === "chapter_pptx" ? "queued" : "running",
                    stage: "queued",
                    step: j.kind === "video" ? "queued" : undefined,
                    progress: 0,
                    current: 0,
                    percent: 0,
                };
            }
        }
        return map;
    } catch {
        return {};
    }
}

function persist(jobs) {
    try {
        const active = Object.values(jobs)
            .filter((j) => j.status === "running" || j.status === "queued")
            .map((j) => ({
                jobId: j.jobId,
                kind: j.kind,
                chapterId: j.chapterId,
                chapterTitle: j.chapterTitle,
                lessonId: j.lessonId,
                lessonTitle: j.lessonTitle,
                numLessons: j.numLessons,
                totalBatches: j.totalBatches,
                total: j.total,
            }));
        localStorage.setItem(STORAGE_KEY, JSON.stringify(active));
    } catch {
        /* localStorage disabled — non-fatal */
    }
}

/* ------------------- provider ------------------- */

export function AiJobsProvider({ children }) {
    const [jobs, setJobs] = useState(() => loadStored());
    // Stable ref so the poll loop sees the latest map without restarting.
    const jobsRef = useRef(jobs);
    jobsRef.current = jobs;
    // Per-job onDone callbacks live outside React state (can't serialise fns).
    const callbacksRef = useRef({});

    /**
     * Register a new background job for tracking.
     * @param {object} args
     * @param {"split"|"quiz"|"video"} args.kind
     * @param {string} args.jobId
     * @param {string} [args.chapterId]    (required for split/quiz)
     * @param {string} [args.chapterTitle]
     * @param {string} [args.lessonId]     (video only)
     * @param {string} [args.lessonTitle]  (video only)
     * @param {number} [args.numLessons]   (split only)
     * @param {number} [args.totalBatches] (split only)
     * @param {number} [args.total]        (video: slide count, quiz: lesson count)
     * @param {Function} [args.onDone]     called once when status → done
     */
    const trackJob = useCallback((args) => {
        const {
            kind, jobId, chapterId, chapterTitle,
            lessonId, lessonTitle,
            numLessons, totalBatches, total, onDone,
        } = args || {};
        if (!jobId || !KIND_CONFIG[kind]) return;
        // split/quiz need a chapterId for their status URLs; video/pptx/chapter_pptx do not.
        if ((kind === "split" || kind === "quiz") && !chapterId) return;
        if (onDone) callbacksRef.current[jobId] = onDone;
        setJobs((prev) => ({
            ...prev,
            [jobId]: {
                kind,
                jobId,
                chapterId,
                chapterTitle: chapterTitle || "Chapter",
                lessonId,
                lessonTitle,
                numLessons: numLessons || undefined,
                totalBatches: totalBatches || 1,
                status: kind === "video" || kind === "pptx" || kind === "chapter_pptx" ? "queued" : "running",
                stage: "queued",
                step: kind === "video" ? "queued" : undefined,
                progress: 0,
                current: 0,
                total: total || (kind === "split" ? (numLessons || 5) : 1),
                current_batch: 0,
                total_batches: totalBatches || 1,
                lessons_created: 0,
                percent: 3,
                startedAt: Date.now(),
            },
        }));
    }, []);

    const dismissJob = useCallback((jobId) => {
        delete callbacksRef.current[jobId];
        setJobs((prev) => {
            const next = { ...prev };
            delete next[jobId];
            return next;
        });
    }, []);

    useEffect(() => {
        persist(jobs);
    }, [jobs]);

    // Single poll loop covers every running job across both kinds.
    useEffect(() => {
        let cancelled = false;
        const tick = async () => {
            const current = jobsRef.current;
            const runningIds = Object.keys(current).filter(
                (id) => current[id].status === "running" || current[id].status === "queued",
            );
            if (!runningIds.length) return;
            const results = await Promise.all(
                runningIds.map(async (id) => {
                    const j = current[id];
                    const cfg = KIND_CONFIG[j.kind];
                    if (!cfg) return [id, null];
                    try {
                        const url = cfg.statusUrl({ chapterId: j.chapterId, jobId: id });
                        const { data } = await api.get(url, { timeout: 20000 });
                        return [id, data];
                    } catch (err) {
                        // 401/403 means the session expired (or we never had
                        // one — e.g. tray was rehydrated from localStorage on
                        // the login page). Mark for prune so the tray
                        // disappears instead of staying stuck at "queued".
                        const code = err?.response?.status;
                        if (code === 401 || code === 403 || code === 404) {
                            return [id, { __prune: true }];
                        }
                        return [id, null];
                    }
                }),
            );
            if (cancelled) return;
            setJobs((prev) => {
                const next = { ...prev };
                for (const [id, data] of results) {
                    if (!data || !next[id]) continue;
                    if (data.__prune) {
                        // Drop jobs we can no longer poll (auth expired / job
                        // GC'd). Persistence will follow on the next render.
                        delete next[id];
                        continue;
                    }
                    const cfg = KIND_CONFIG[next[id].kind];
                    if (!cfg) continue;
                    const merged = {
                        ...next[id],
                        ...data,
                        chapterTitle: next[id].chapterTitle || data.chapter_title,
                    };
                    merged.percent = cfg.computePercent(next[id], merged);
                    if (
                        data.status === "done"
                        && next[id].status !== "done"
                    ) {
                        toast.success(cfg.successMessage(merged, data));
                        const cb = callbacksRef.current[id];
                        if (typeof cb === "function") {
                            try { cb(data); } catch { /* ignore */ }
                        }
                    } else if (
                        data.status === "failed"
                        && next[id].status !== "failed"
                    ) {
                        toast.error(
                            data.error || `AI job failed for "${merged.chapterTitle}".`,
                        );
                    }
                    next[id] = merged;
                }
                return next;
            });
        };
        const handle = setInterval(tick, POLL_MS);
        tick(); // tick immediately so re-hydrated jobs refresh fast
        return () => {
            cancelled = true;
            clearInterval(handle);
        };
    }, []);

    const value = {
        jobs,
        trackJob,
        dismissJob,
        getStageLabel: (job) => {
            const cfg = KIND_CONFIG[job.kind];
            return cfg ? cfg.stageLabel(job) : "Working…";
        },
        getKindLabel: (job) => KIND_CONFIG[job.kind]?.label || "AI job",
        activeCount: Object.values(jobs).filter(
            (j) => j.status === "running" || j.status === "queued",
        ).length,
    };
    return (
        <AiJobsContext.Provider value={value}>{children}</AiJobsContext.Provider>
    );
}

export function useAiJobs() {
    const ctx = useContext(AiJobsContext);
    if (!ctx) throw new Error("useAiJobs must be used inside <AiJobsProvider>");
    return ctx;
}
